import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../../api";
import { Badge, PlanBadge } from "../../../components/badge";
import { BigTextPreview } from "../../../components/big-text-preview";
import { Button, ButtonRow, DangerButton, PrimaryButton } from "../../../components/button";
import { Card, CardContent, CardGrid, CardHeader } from "../../../components/card";
import { AreaChart, PieChart } from "../../../components/chart";
import { CodeEditor } from "../../../components/code-editor";
import { CommandBar } from "../../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../../components/dashboard";
import { DateTimeLabel } from "../../../components/datetime-label";
import { Editor, EditorDescription, EditorRow, EditorTitle } from "../../../components/editor";
import { Checkbox, FormControl, NumberInput, Select, TextInput } from "../../../components/form";
import { Icon } from "../../../components/icon";
import { Content, Section } from "../../../components/layout";
import { Meter } from "../../../components/meter";
import { MonthlyReport } from "../../../components/monthly-report";
import { RelativeTimeRangePicker } from "../../../components/relative-time-range";
import { SpecializedControl } from "../../../components/specializedObject";
import { TabControl } from "../../../components/tabcontrol";
import { DefaultRow, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableHorizontalBar, TableRow } from "../../../components/table";
import { CheckWrapper, CopiableText, Heading, InlineCode, MutedText, Paragraph } from "../../../components/text";
import { displayAlert, displayFormControl } from "../../../ui/alert";
import { formatCurrency, formatDate, formatDateTime, formatFloat, formatNumber, formatPreciseCurrency, formatVeryPreciseCurrency } from "../../../ui/format";
import { showFlyout } from "../../../ui/flyout";
import { renderMarkdown } from "../../../ui/md-renderer";
import { runOperation } from "../../../ui/operation";
import { getUniqueColor } from "../../../ui/unique-color";
import { cond, find } from "../../../util";
import { Link } from "../../../components/link";
import { ModelBadge } from "../../../components/model-icon";

const ContractLabels = {
    "pt-br": {
        title: "Acordo de serviços AIVAX",
        account: "Conta",
        issuedAt: "Emitido em",
        provider: "Fornecedor",
        customer: "Cliente",
        legalIdentifier: "CPF/CNPJ",
        address: "Endereço",
        country: "País",
        intro: "Este acordo registra as condições negociadas entre a AIVAX e o cliente para o uso da plataforma AIVAX pela conta acima. As demais regras de uso estão nos Termos de Uso, na Política de Privacidade e na lista de Processadores de Dados.",
        agreedTitle: "1. Condições negociadas",
        noAgreedTerms: "Não há condições específicas negociadas. Aplicam-se integralmente os documentos de referência.",
        agreedFixed: "Estas condições ficam fixadas neste acordo e só mudam por um novo acordo entre as partes.",
        annexesTitle: "2. Documentos de referência",
        annexesText: "Também se aplicam ao uso da plataforma os seguintes documentos, na versão vigente publicada pela AIVAX:",
        annexesCopy: "Esses documentos podem ser atualizados pela AIVAX. As cópias anexas refletem a versão disponível na data de emissão e servem apenas como referência; a versão vigente é sempre a publicada no endereço indicado em cada anexo.",
        annexTitles: ["Termos de Uso", "Política de Privacidade", "Processadores de Dados"],
        precedence: "Se houver conflito, as condições negociadas prevalecem sobre esses documentos, inclusive sobre versões futuras.",
        validityTitle: "3. Aceitação e vigência",
        acceptedValidity: "O cliente aceita este acordo ao utilizar a plataforma AIVAX, sem necessidade de assinatura. O acordo vale enquanto a conta estiver em uso, até ser substituído por um novo acordo emitido ao cliente.",
        signedValidity: "Este acordo entra em vigor na data da assinatura e vale enquanto a conta estiver em uso, até ser substituído por um novo acordo assinado pelas partes.",
        lawTitle: "4. Legislação e foro",
        lawText: "Este acordo é regido pelas leis brasileiras, com foro eleito na Comarca de São José do Rio Preto/SP.",
        signatures: "Assinaturas",
        place: "Local e data",
        witness: "Testemunha",
        witnessFields: "Nome e CPF",
        annex: "Anexo",
        source: "Versão vigente",
        retrievedAt: "cópia de referência obtida em"
    },
    en: {
        title: "AIVAX service agreement",
        account: "Account",
        issuedAt: "Issued at",
        provider: "Provider",
        customer: "Customer",
        legalIdentifier: "Tax ID",
        address: "Address",
        country: "Country",
        intro: "This agreement records the terms negotiated between AIVAX and the customer for the use of the AIVAX platform by the account above. All other usage rules are set out in the Terms of Use, the Privacy Policy and the Data Processors list.",
        agreedTitle: "1. Negotiated terms",
        noAgreedTerms: "No specific terms were negotiated. The reference documents apply in full.",
        agreedFixed: "These terms are fixed by this agreement and only change through a new agreement between the parties.",
        annexesTitle: "2. Reference documents",
        annexesText: "The following documents also apply to the use of the platform, in the current version published by AIVAX:",
        annexesCopy: "These documents may be updated by AIVAX. The attached copies reflect the version available on the issue date and are for reference only; the current version is always the one published at the address shown in each annex.",
        annexTitles: ["Terms of Use", "Privacy Policy", "Data Processors"],
        precedence: "In the event of a conflict, the negotiated terms prevail over these documents, including future versions.",
        validityTitle: "3. Acceptance and term",
        acceptedValidity: "The customer accepts this agreement by using the AIVAX platform, and no signature is required. The agreement remains in force while the account is in use, until it is replaced by a new agreement issued to the customer.",
        signedValidity: "This agreement takes effect on the date it is signed and remains in force while the account is in use, until it is replaced by a new agreement signed by the parties.",
        lawTitle: "4. Governing law and jurisdiction",
        lawText: "This agreement is governed by Brazilian law, and the courts of São José do Rio Preto/SP, Brazil, are elected to settle any disputes.",
        signatures: "Signatures",
        place: "Place and date",
        witness: "Witness",
        witnessFields: "Name and ID",
        annex: "Annex",
        source: "Current version",
        retrievedAt: "reference copy retrieved at"
    }
};

export default function (ctx) {
    const accountId = ctx.params.accountId;
    const account = el.state.create(null);
    const usage = el.state.create(null);

    function loadAccount() {
        runOperation("Loading account...", async () => {
            const result = await api.getJson(`/api/v1/admin/accounts/${accountId}`);
            account.value = result.data;
        });
    }

    function loadUsage() {
        runOperation("Loading account usage...", async () => {
            const timeRange = find("adminUsageTimeRange")?.value;
            const params = new URLSearchParams();
            if (timeRange?.start) {
                params.set("start", timeRange.start);
            }
            if (timeRange?.end) {
                params.set("end", timeRange.end);
            }
            const result = await api.getJson(`/api/v1/admin/accounts/${accountId}/usage?${params.toString()}`);
            usage.value = result.data;
        });
    }

    async function updateAccount(payload) {
        const result = await api.patchJson(`/api/v1/admin/accounts/${accountId}`, payload);
        account.value = result.data;
        return result.data;
    }

    async function updateAccountTerms(payload) {
        const result = await api.patchJson(`/api/v1/admin/accounts/${accountId}`, payload);
        // Mutates the loaded account silently: assigning account.value would re-render the TabControl back to its first tab.
        Object.assign(account.value, result.data);
        return result.data;
    }

    async function applyBalanceAdjustment(payload) {
        const result = await api.postJson(`/api/v1/admin/accounts/${accountId}/balance-adjustments`, payload);
        account.value = result.data.account;
        return result.data;
    }

    function showBalanceAdjustment(mode) {
        const isSet = mode === "set";
        const title = cond(
            [mode === "add", "Add balance"],
            [mode === "remove", "Remove balance"],
            [mode === "set", "Set balance"]
        );

        displayFormControl({
            title,
            message: isSet
                ? "Set the account balance to an exact target amount."
                : `${title} through a paid adjustment invoice.`,
            formControlItem: SpecializedControl({
                fields: [
                    {
                        name: "amount",
                        label: isSet ? "Target balance" : "Amount",
                        control: el("input.app-input[form-slot][type=number]", {
                            value: isSet ? Math.max(account.value?.currentBalance || 0, 0) : 1,
                            min: 0,
                            max: 1_000_000,
                            step: 0.01
                        })
                    },
                    {
                        name: "description",
                        label: "Description",
                        control: TextInput(null, "Adjustment reason")
                    },
                    {
                        name: "durationDays",
                        label: "Invoice validity (days)",
                        control: el("input.app-input[form-slot][type=number]", {
                            value: 365,
                            min: 1,
                            max: 3650,
                            step: 1
                        })
                    }
                ]
            }),
            validate: [
                [value => mode === "set" ? Number(value.amount) >= 0 : Number(value.amount) > 0, "Amount must be valid for the selected action."],
                [value => Number(value.durationDays) > 0, "Invoice validity must be greater than zero days."]
            ],
            onConfirm(value) {
                const payload = {
                    mode,
                    amount: Number(value.amount),
                    description: value.description,
                    durationDays: Number(value.durationDays)
                };
                const operation = () => runOperation("Adjusting balance...", async () => {
                    await applyBalanceAdjustment(payload);
                });

                if (mode === "add") {
                    operation();
                    return;
                }

                showFlyout(
                    Paragraph(mode === "set"
                        ? `Set this account balance to ${formatCurrency(payload.amount)}?`
                        : `Remove ${formatCurrency(payload.amount)} from this account?`),
                    DangerButton("Confirm adjustment", {
                        onClick() {
                            operation();
                        }
                    })
                );
            }
        });
    }

    function resourceTypeName(resourceGroup) {
        const type = Array.isArray(resourceGroup.type) ? resourceGroup.type[0] : resourceGroup.type;
        return cond(
            [type === "AiGateway", el.fragment(Icon("route-line"), " AI Gateways")],
            [type === "Collection", el.fragment(Icon("book-shelf-line"), " Collections")],
            [type === "ChatClient", el.fragment(Icon("chat-1-line"), " Chat clients")],
            [type === "ChatClientSession", el.fragment(Icon("chat-quote-line"), " Chat sessions")],
            [type === "ApiKey", el.fragment(Icon("key-2-line"), " API keys")],
            [type === "Unknown", el.fragment(Icon("question-line"), " Other")],
            [true, type || "Other"]
        );
    }

    function renderPieFromItems(items, getLabel, getValue) {
        if (!items?.length) {
            return MutedText("No usage data.");
        }

        const sorted = [...items].sort((a, b) => getValue(b) - getValue(a));
        const top = sorted.slice(0, 6);
        const rest = sorted.slice(6).reduce((sum, item) => sum + getValue(item), 0);
        const labels = top.map(getLabel);
        const series = top.map(getValue);
        if (rest > 0) {
            labels.push("Others");
            series.push(rest);
        }

        return PieChart({ series, labels, height: 300 });
    }

    function formatBytes(value) {
        if (value >= 1024 ** 3)
            return `${formatFloat(value / 1024 ** 3)} GB`;

        if (value >= 1024 ** 2)
            return `${formatFloat(value / 1024 ** 2)} MB`;

        if (value >= 1024)
            return `${formatFloat(value / 1024)} KB`;

        return `${formatNumber(value)} B`;
    }

    function renderSubscriptionUsageTable(consumption) {
        return Table(
            { classList: ["hoverable"], pagination: { perPage: 8 } },
            TableHeader(
                TableHeaderCell("Usage"),
                TableHeaderCell("Share")
            ),
            TableBody(
                consumption.details.map(detail =>
                    TableRow(
                        TableCell(detail.usageName),
                        TableCell(formatFloat(detail.usageSharePercent), "%")
                    )
                )?.nullWhenEmpty() || DefaultRow("No subscription usage in this window."),
                {
                    mount() {
                        this.closest("table").refreshPagination();
                    }
                }
            )
        );
    }

    loadAccount();
    loadUsage();

    return Dashboard(
        Section(
            Link(el.fragment(Icon("arrow-left-line"), "Return back"), null, {
                onClick() {
                    history.go(-1);
                    page();
                }
            })
        ),
        el.state.listening([account], () =>
            DashboardTitle(
                account.value?.name || "Admin account",
                "You are managing the " + account.value?.name + " account."
            )
        ),
        Section(
            CommandBar(
                FormControl("Date range",
                    RelativeTimeRangePicker({
                        id: "adminUsageTimeRange",
                        onInput() {
                            loadUsage();
                        }
                    })
                ),
                Button(Icon("refresh-line"), "Refresh", {
                    onClick() {
                        loadAccount();
                        loadUsage();
                    }
                })
            )
        ),
        Section(
            el.state.listening([usage, account], () => {
                const data = account.value;
                const subscriptionConsumptions = data?.subscriptionModelUsage?.consumptions || [];
                const meterProps = {
                    min: 0,
                    max: 100,
                    colorShade: true,
                    props: {
                        classList: ["full-width"]
                    }
                };
                const storagePercent = data?.planLimits?.includedStorage > 0
                    ? Math.min((data.storageUsage || 0) / data.planLimits.includedStorage * 100, 100)
                    : 0;

                if (!data || !usage.value) {
                    return el("div");
                }

                return TabControl([
                    data && {
                        title: "Overview",
                        icon: Icon("dashboard-line"),
                        contents: Content(
                            Section(
                                CardGrid(
                                    Card(
                                        CardHeader("Total added"),
                                        CardContent(el("h2", { style: { margin: 0, fontWeight: 400 } }, formatCurrency(data.totalAdded)))
                                    ),
                                    Card(
                                        CardHeader("Current plan"),
                                        CardContent(el("h2", { style: { margin: 0, fontWeight: 400 } }, PlanBadge(data.plan)))
                                    ),
                                    Card(
                                        CardHeader("Tax multiplier"),
                                        CardContent(el("h2", { style: { margin: 0, fontWeight: 400 } }, data.accountTaxMultiplier.toFixed(2), "x"))
                                    )
                                )
                            ),
                            Heading("Account controls"),
                            Section(
                                Editor(
                                    {
                                        value: data.email,
                                        readonly: true,
                                        renderValue(value) {
                                            return value;
                                        },
                                        manipulateValue(value, callback) {
                                            ;
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("E-mail"),
                                        Paragraph("Defines the account main e-mail. This field is read-only.")
                                    )
                                ),
                                Editor(
                                    {
                                        value: data.plan,
                                        renderValue(value) {
                                            return PlanBadge(value);
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: Select({
                                                    value,
                                                    items: [
                                                        { value: "Free", text: "Free" },
                                                        { value: "Pro", text: "Pro" },
                                                        { value: "Max", text: "Max" },
                                                        { value: "Custom", text: "Custom" }
                                                    ]
                                                }),
                                                onConfirm(newPlan) {
                                                    runOperation("Updating account plan...", async () => {
                                                        const updatedAccount = await updateAccount({ plan: newPlan });
                                                        callback(updatedAccount.plan);
                                                    });
                                                }
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Plan"),
                                        Paragraph("Controls account limits, included resources and commission rate.")
                                    )
                                ),
                                Editor(
                                    {
                                        value: data.accountTaxMultiplier,
                                        renderValue(value) {
                                            return Button(`${formatFloat(value)}x`);
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({
                                                    value,
                                                    min: 0.01,
                                                    max: 10,
                                                    step: 0.01
                                                }),
                                                validate: [
                                                    [newValue => newValue > 0, "Multiplier must be greater than zero."]
                                                ],
                                                onConfirm(newMultiplier) {
                                                    runOperation("Updating tax multiplier...", async () => {
                                                        const updatedAccount = await updateAccount({ accountTaxMultiplier: newMultiplier });
                                                        callback(updatedAccount.accountTaxMultiplier);
                                                    });
                                                }
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Tax multiplier"),
                                        Paragraph("Multiplies usage pricing before plan commission is applied.")
                                    )
                                ),
                                Editor(
                                    {
                                        value: data.limitMultiplier,
                                        renderValue(value) {
                                            return Button(value == null ? MutedText("Unlimited") : `${formatFloat(value)}x`);
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({
                                                    value: value ?? 1,
                                                    min: 0.01,
                                                    max: 1000,
                                                    step: 0.01
                                                }),
                                                nullButton: true,
                                                validate: [
                                                    [newValue => newValue > 0, "Multiplier must be greater than zero."]
                                                ],
                                                onConfirm(newMultiplier) {
                                                    runOperation("Updating limit multiplier...", async () => {
                                                        const updatedAccount = await updateAccount({ limitMultiplier: newMultiplier });
                                                        callback(updatedAccount.limitMultiplier);
                                                    });
                                                }
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Limit multiplier"),
                                        Paragraph("Multiplies the plan rate limits.")
                                    )
                                ),
                                EditorRow(
                                    ButtonRow(
                                        Button(Icon("speed-up-line"), "View rate limits", {
                                            onClick() {
                                                page(`/dashboard/admin/accounts/${accountId}/rate-limits`);
                                            }
                                        })
                                    )
                                )
                            ),
                            Heading("Subscription", Paragraph("Billing settings applied by the monthly renewal of paid plans.")),
                            Section(
                                Editor(
                                    {
                                        value: data.planMonthlyPrice,
                                        renderValue(value) {
                                            return Button(value == null ? MutedText(`Plan price (${formatCurrency(data.effectiveMonthlyPrice ?? 0)}/month)`) : `${formatCurrency(value)}/month`);
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({
                                                    value: value ?? data.effectiveMonthlyPrice ?? 0,
                                                    min: 0,
                                                    max: 1000000,
                                                    step: 0.01
                                                }),
                                                nullButton: true,
                                                validate: [
                                                    [newValue => newValue >= 0, "The price cannot be negative."]
                                                ],
                                                onConfirm(newPrice) {
                                                    runOperation("Updating monthly price...", async () => {
                                                        const updatedAccount = await updateAccount({ planMonthlyPrice: newPrice });
                                                        callback(updatedAccount.planMonthlyPrice);
                                                    });
                                                }
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Negotiated monthly price"),
                                        Paragraph("USD charged at each renewal instead of the plan list price. Zero means the plan is never charged. Does not change the current plan.")
                                    )
                                ),
                                Editor(
                                    {
                                        value: data.planStartAt,
                                        renderValue(value) {
                                            return Button(value == null ? MutedText("Immediately") : formatDate(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: el("input.app-input[type=date]", { value: value ?? "" }),
                                                nullButton: true,
                                                validate: [
                                                    [newValue => !!newValue, "Choose a date."]
                                                ],
                                                onConfirm(newDate) {
                                                    runOperation("Updating billing start...", async () => {
                                                        const updatedAccount = await updateAccount({ planStartAt: newDate });
                                                        callback(updatedAccount.planStartAt);
                                                    });
                                                }
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Billing start"),
                                        Paragraph("No renewal is charged before this date. Leave empty to bill from the current cycle.")
                                    )
                                ),
                                Editor(
                                    {
                                        value: data.pendingPlanModification,
                                        renderValue(value) {
                                            return Button(value == null ? MutedText("None") : InlineCode(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: Select({
                                                    value: value ?? "",
                                                    items: [
                                                        { value: "", text: "None" },
                                                        { value: "cancel", text: "Cancel at the next renewal" },
                                                        { value: "newplan:Pro", text: "Change to Pro at the next renewal" },
                                                        { value: "newplan:Max", text: "Change to Max at the next renewal" }
                                                    ]
                                                }),
                                                onConfirm(newValue) {
                                                    runOperation("Updating pending modification...", async () => {
                                                        const updatedAccount = await updateAccount({ pendingPlanModification: newValue.nullWhenEmpty() });
                                                        callback(updatedAccount.pendingPlanModification);
                                                    });
                                                }
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Pending plan modification"),
                                        Paragraph("Applied by the renewal job on the first day of the next cycle, before any charge.")
                                    )
                                ),
                                Editor(
                                    {
                                        value: data.activeSubscription,
                                        readonly: true,
                                        hoverState: false,
                                        renderValue(value) {
                                            return value == null
                                                ? MutedText("No paid subscription in the current cycle")
                                                : el("div",
                                                    `${formatCurrency(value.amount)} paid on ${formatDateTime(value.paidAt)}`,
                                                    el("div", MutedText(`Cycle ${formatDate(value.cycleStart)} to ${formatDate(value.cycleEnd)}`), " ", CopiableText("#" + value.invoiceId.substr(-4), value.invoiceId))
                                                );
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Active subscription"),
                                        Paragraph("The paid subscription invoice covering the current calendar month.")
                                    )
                                )
                            ),
                            Heading("Account balance and details"),
                            Section(
                                Editor(
                                    {
                                        value: data.currentBalance,
                                        readonly: true,
                                        hoverState: false,
                                        renderValue(value) {
                                            return Number(value) === 0 ? MutedText("-") : el("h3", formatCurrency(value))
                                        },
                                        manipulateValue(value, callback) {
                                            ;
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Current balance"),
                                        Paragraph("Current account balance.")
                                    )
                                ),
                                EditorRow(
                                    ButtonRow(
                                        Button(Icon("add-circle-line"), "Add balance", {
                                            onClick() {
                                                showBalanceAdjustment("add");
                                            }
                                        }),
                                        Button(Icon("subtract-line"), "Remove balance", {
                                            onClick() {
                                                showBalanceAdjustment("remove");
                                            }
                                        }),
                                        Button(Icon("equal-line"), "Set balance", {
                                            onClick() {
                                                showBalanceAdjustment("set");
                                            }
                                        })
                                    )
                                )
                            )
                        )
                    },
                    data && {
                        title: "Advanced",
                        icon: Icon("settings-3-line"),
                        contents: Content(
                            Heading("Advanced controls"),
                            Section(
                                Editor(
                                    {
                                        value: data.disabledAt == null,
                                        renderValue(value) {
                                            return CheckWrapper(value, "Active", "Disabled");
                                        },
                                        manipulateValue(value, callback) {
                                            if (!value) {
                                                runOperation("Enabling account...", async () => {
                                                    const updatedAccount = await updateAccount({ disabled: false });
                                                    callback(updatedAccount.disabledAt == null);
                                                });
                                                return;
                                            }

                                            showFlyout(
                                                Paragraph("Disable this account? API access, authentication and login will stop immediately."),
                                                DangerButton(Icon("forbid-2-line"), "Disable account", {
                                                    onClick() {
                                                        runOperation("Disabling account...", async () => {
                                                            const updatedAccount = await updateAccount({ disabled: true });
                                                            callback(updatedAccount.disabledAt == null);
                                                        });
                                                    }
                                                })
                                            );
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Account status"),
                                        data.disabledAt
                                            ? Paragraph("Disabled since ", DateTimeLabel(data.disabledAt), ".")
                                            : Paragraph("This account is active.")
                                    )
                                )
                            )
                        )
                    },
                    data && {
                        title: "Contact & terms",
                        icon: Icon("contacts-book-line"),
                        contents: Content(
                            Heading("Contact information"),
                            Section(
                                Editor(
                                    {
                                        value: data.contactInformation?.legalName,
                                        renderValue(value) {
                                            return Button(value || MutedText("None"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Legal name", { value: value || "" }),
                                                validate: [
                                                    [newValue => newValue.length <= 256, "Legal name must have at most 256 characters."]
                                                ],
                                                onConfirm(newValue) {
                                                    runOperation("Updating legal name...", async () => {
                                                        const updatedAccount = await updateAccountTerms({ contactInformation: { legalName: newValue.trim().nullWhenEmpty() } });
                                                        callback(updatedAccount.contactInformation?.legalName);
                                                    });
                                                }
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Legal name"),
                                        Paragraph("Full legal name of the person or company that owns this account.")
                                    )
                                ),
                                Editor(
                                    {
                                        value: data.contactInformation?.legalIdentifier,
                                        renderValue(value) {
                                            return Button(value || MutedText("None"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "CPF, CNPJ or equivalent", { value: value || "" }),
                                                validate: [
                                                    [newValue => newValue.length <= 64, "Legal identifier must have at most 64 characters."]
                                                ],
                                                onConfirm(newValue) {
                                                    runOperation("Updating legal identifier...", async () => {
                                                        const updatedAccount = await updateAccountTerms({ contactInformation: { legalIdentifier: newValue.trim().nullWhenEmpty() } });
                                                        callback(updatedAccount.contactInformation?.legalIdentifier);
                                                    });
                                                }
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Legal identifier"),
                                        Paragraph("Tax or registration identifier, such as CPF or CNPJ.")
                                    )
                                ),
                                Editor(
                                    {
                                        value: data.contactInformation?.legalAddress,
                                        renderValue(value) {
                                            return Button(value || MutedText("None"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Street, number, city, state, postal code", { value: value || "" }),
                                                validate: [
                                                    [newValue => newValue.length <= 512, "Legal address must have at most 512 characters."]
                                                ],
                                                onConfirm(newValue) {
                                                    runOperation("Updating legal address...", async () => {
                                                        const updatedAccount = await updateAccountTerms({ contactInformation: { legalAddress: newValue.trim().nullWhenEmpty() } });
                                                        callback(updatedAccount.contactInformation?.legalAddress);
                                                    });
                                                }
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Legal address"),
                                        Paragraph("Registered address of the person or company.")
                                    )
                                ),
                                Editor(
                                    {
                                        value: data.contactInformation?.legalAddressCountry,
                                        renderValue(value) {
                                            return Button(value || MutedText("None"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "BR", { value: value || "" }),
                                                validate: [
                                                    [newValue => /^([A-Za-z]{2})?$/.test(newValue.trim()), "Country must be a two-letter ISO country code."]
                                                ],
                                                onConfirm(newValue) {
                                                    runOperation("Updating legal address country...", async () => {
                                                        const updatedAccount = await updateAccountTerms({ contactInformation: { legalAddressCountry: newValue.trim().toUpperCase().nullWhenEmpty() } });
                                                        callback(updatedAccount.contactInformation?.legalAddressCountry);
                                                    });
                                                }
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Legal address country"),
                                        Paragraph("ISO 3166-1 alpha-2 country code of the legal address, such as BR or US.")
                                    )
                                )
                            ),
                            Heading("Agreement"),
                            Section(
                                Editor(
                                    {
                                        value: data.agreement,
                                        renderValue(value) {
                                            return BigTextPreview(value);
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: CodeEditor({ value: value || "", language: "markdown", height: "420px" }),
                                                validate: [
                                                    [newValue => String(newValue || "").length <= 65536, "Agreement must have at most 65536 characters."]
                                                ],
                                                onConfirm(newValue) {
                                                    runOperation("Updating agreement...", async () => {
                                                        const updatedAccount = await updateAccountTerms({ agreement: String(newValue || "").trim().nullWhenEmpty() });
                                                        callback(updatedAccount.agreement);
                                                    });
                                                }
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Agreement"),
                                        Paragraph("Markdown with the rules, agreements and fees negotiated with this customer.")
                                    )
                                )
                            )
                        )
                    },
                    data && {
                        title: "Agreement",
                        icon: Icon("file-paper-2-line"),
                        contents: Content(
                            Section(
                                Heading(
                                    "Service agreement",
                                    Paragraph("Open a print-ready agreement in a new tab with the customer and AIVAX contact details, the negotiated terms (fixed by the agreement) and reference copies of the current Terms of Use, Privacy Policy and Data Processors. By default the customer accepts it by using the platform; require signatures to add signature and witness fields.")
                                ),
                                CommandBar(
                                    FormControl("Language",
                                        Select({
                                            id: "accountContractLanguage",
                                            value: data.contactInformation?.legalAddressCountry === "BR" ? "pt-br" : "en",
                                            items: [
                                                { value: "pt-br", text: "Português (Brasil)" },
                                                { value: "en", text: "English" }
                                            ]
                                        })
                                    ),
                                    FormControl("Signatures",
                                        Checkbox("accountContractSignatures", "Require signatures from the parties")
                                    ),
                                    PrimaryButton(
                                        Icon("file-paper-2-line"),
                                        "Generate agreement",
                                        {
                                            onClick() {
                                                // Opened before any await so popup blockers treat it as part of the click.
                                                const contractWindow = window.open("", "_blank");

                                                runOperation("Generating agreement...", async () => {
                                                    if (!contractWindow) {
                                                        throw new Error("The browser blocked the agreement tab. Allow pop-ups for this site and try again.");
                                                    }

                                                    const language = find("accountContractLanguage").value;
                                                    const requireSignatures = document.querySelector("input[name=accountContractSignatures]").checked;
                                                    const labels = ContractLabels[language];
                                                    const customer = account.value;
                                                    const contact = customer.contactInformation ?? {};
                                                    const issuedAt = formatDateTime(new Date());

                                                    const documents = await Promise.all(
                                                        ["terms-of-service", "privacy-policy", "third-party-processors"].map(async name => {
                                                            const url = `https://docs.aivax.net/${language === "pt-br" ? "pt-br/" : ""}docs/legal/${name}`;
                                                            const response = await fetch(`${url}.md`, { cache: "no-store" });
                                                            if (!response.ok) {
                                                                throw new Error(`Could not load the ${name} document (HTTP ${response.status}).`);
                                                            }
                                                            return {
                                                                url: `${url}.html`,
                                                                markdown: (await response.text()).replace(/^\s*Source: \S+\s*(#[^\n]*\n)?/, "")
                                                            };
                                                        })
                                                    );

                                                    contractWindow.document.open();
                                                    contractWindow.document.write("<!DOCTYPE html>" + el("html", { lang: language },
                                                        el("head",
                                                            el("meta", { charset: "utf-8" }),
                                                            el("title", `${labels.title} - ${contact.legalName || customer.name}`),
                                                            el("link", { rel: "stylesheet", href: `${location.origin}/assets/account-contract.css` })
                                                        ),
                                                        el("body",
                                                            el("header",
                                                                el("img", { src: `${location.origin}/assets/aivax-alt-inverted.png`, alt: "AIVAX" }),
                                                                el("dl",
                                                                    el("dt", labels.account), el("dd", customer.id),
                                                                    el("dt", labels.issuedAt), el("dd", issuedAt)
                                                                )
                                                            ),
                                                            el("h1", labels.title),
                                                            el(".parties",
                                                                el("div",
                                                                    el(".label", labels.provider),
                                                                    el("strong", "Project Principium Soluções em Nuvem LTDA"),
                                                                    el("span", "CNPJ 48.729.809/0001-85"),
                                                                    el("span", "wm@aivax.net · legal@aivax.net")
                                                                ),
                                                                el("div",
                                                                    el(".label", labels.customer),
                                                                    el("strong", contact.legalName || customer.name),
                                                                    el("span", `${labels.legalIdentifier} `, contact.legalIdentifier || el("span.blank")),
                                                                    el("span", `${labels.address} `, contact.legalAddress || el("span.blank")),
                                                                    el("span", `${labels.country} `, contact.legalAddressCountry
                                                                        ? new Intl.DisplayNames([language], { type: "region" }).of(contact.legalAddressCountry)
                                                                        : el("span.blank")),
                                                                    el("span", customer.email)
                                                                )
                                                            ),
                                                            el("p.intro", labels.intro),
                                                            el("section.clause",
                                                                el("h2", labels.agreedTitle),
                                                                customer.agreement
                                                                    ? [
                                                                        el(".markdown", el.raw(renderMarkdown(customer.agreement))),
                                                                        el("p.note", labels.agreedFixed)
                                                                    ]
                                                                    : el("p", labels.noAgreedTerms)
                                                            ),
                                                            el("section.clause",
                                                                el("h2", labels.annexesTitle),
                                                                el("p", labels.annexesText),
                                                                el("ol", { type: "I" }, labels.annexTitles.map(title => el("li", title))),
                                                                el("p", labels.annexesCopy),
                                                                el("p", labels.precedence)
                                                            ),
                                                            el("section.clause",
                                                                el("h2", labels.validityTitle),
                                                                el("p", requireSignatures ? labels.signedValidity : labels.acceptedValidity)
                                                            ),
                                                            el("section.clause",
                                                                el("h2", labels.lawTitle),
                                                                el("p", labels.lawText)
                                                            ),
                                                            requireSignatures && el("section.closing",
                                                                el(".label", labels.signatures),
                                                                el(".signatures",
                                                                    el("div", el("strong", "Project Principium Soluções em Nuvem LTDA"), el("span", labels.provider)),
                                                                    el("div", el("strong", contact.legalName || customer.name), el("span", labels.customer)),
                                                                    [1, 2].map(number => el("div", el("strong", `${labels.witness} ${number}`), el("span", labels.witnessFields))),
                                                                    el("div", el("strong", labels.place))
                                                                )
                                                            ),
                                                            documents.map((annex, index) =>
                                                                el("section.annex",
                                                                    el(".label", `${labels.annex} ${["I", "II", "III"][index]}`),
                                                                    el("h1", labels.annexTitles[index]),
                                                                    el("p.source", `${labels.source}: ${annex.url} — ${labels.retrievedAt} ${issuedAt}`),
                                                                    el(".markdown", el.raw(renderMarkdown(annex.markdown)))
                                                                )
                                                            )
                                                        )
                                                    ).outerHTML);
                                                    contractWindow.document.close();

                                                    const missingFields = [
                                                        ["legal name", contact.legalName],
                                                        ["legal identifier", contact.legalIdentifier],
                                                        ["legal address", contact.legalAddress],
                                                        ["legal address country", contact.legalAddressCountry]
                                                    ].filter(([, value]) => !value).map(([name]) => name);

                                                    if (missingFields.length > 0) {
                                                        displayAlert({
                                                            title: "Agreement",
                                                            message: `This customer has no ${missingFields.join(", ")}. These fields were left blank in the agreement; fill them in the Contact & terms tab before sending it to the customer.`,
                                                            type: "alert-warning"
                                                        });
                                                    }
                                                }).catch(error => {
                                                    contractWindow?.close();
                                                    displayAlert({
                                                        title: "Agreement",
                                                        message: error.message,
                                                        type: "alert-warning"
                                                    });
                                                });
                                            }
                                        }
                                    )
                                )
                            )
                        )
                    },
                    data && {
                        title: "Subscription usage",
                        icon: Icon("cloud-line"),
                        contents: Content(
                            Section(
                                Editor(
                                    {
                                        readonly: true,
                                        hoverState: false,
                                        renderValue() {
                                            return Meter({ value: storagePercent, ...meterProps });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Storage usage"),
                                        Paragraph(`${formatBytes(data.storageUsage || 0)} / ${formatBytes(data.planLimits?.includedStorage || 0)} used`)
                                    )
                                ),
                                subscriptionConsumptions.length > 0 && el.fragment(
                                    ...subscriptionConsumptions.map(consumption =>
                                        Editor(
                                            {
                                                readonly: true,
                                                hoverState: false,
                                                renderValue() {
                                                    return Meter({ value: Math.min(consumption.usedPercent, 100), ...meterProps });
                                                }
                                            },
                                            EditorDescription(
                                                EditorTitle(consumption.name),
                                                Paragraph(
                                                    `${formatFloat(consumption.usedPercent)}% used. `,
                                                    consumption.resetAt ? `Resets at ${formatDateTime(consumption.resetAt)}.` : "No active consumption window."
                                                )
                                            )
                                        )
                                    ),
                                    el("div", {
                                        style: {
                                            display: "grid",
                                            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                                            gap: "16px",
                                            marginTop: "1em"
                                        }
                                    },
                                        ...subscriptionConsumptions.map(consumption =>
                                            Card(
                                                { classList: ["bg-card"] },
                                                CardHeader(consumption.name),
                                                CardContent(renderSubscriptionUsageTable(consumption))
                                            )
                                        )
                                    )
                                ) || MutedText("This plan does not include subscription usage.")
                            )
                        )
                    },
                    {
                        title: "Usage per model",
                        icon: Icon("brain-line"),
                        contents: Content(
                            Section(
                                el("div", {
                                    style: {
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                                        gap: "24px",
                                        alignItems: "center",
                                        width: "100%"
                                    }
                                },
                                    usage.value.charts?.models?.series?.length && AreaChart({
                                        series: usage.value.charts.models.series,
                                        xaxis: { type: "datetime", tickAmount: 4 },
                                        yaxis: { show: false, labels: { show: false } }
                                    }) || MutedText("No usage data."),
                                    renderPieFromItems(
                                        usage.value.modelUsageDiscriminations,
                                        item => item.modelName,
                                        item => item.totalUsage
                                    )
                                )
                            ),
                            Section(
                                Table(
                                    { classList: ["hoverable"], pagination: { perPage: 20 } },
                                    TableHeader(
                                        TableHeaderCell("Model"),
                                        TableHeaderCell("SKU"),
                                        TableHeaderCell("Quantity"),
                                        TableHeaderCell("Total usage")
                                    ),
                                    TableBody(
                                        usage.value.modelUsageDiscriminations.map(item =>
                                            TableRow(
                                                TableCell(ModelBadge(item.modelName)),
                                                TableCell(InlineCode(item.category)),
                                                TableCell(formatFloat(item.quantity)),
                                                TableCell(formatPreciseCurrency(item.totalUsage), { title: item.totalUsage })
                                            )
                                        )?.nullWhenEmpty() || DefaultRow("No model usage in this period.")
                                    )
                                )
                            )
                        )
                    },
                    {
                        title: "Usage per SKU",
                        icon: Icon("price-tag-3-line"),
                        contents: Content(
                            Section(
                                el("div", {
                                    style: {
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                                        gap: "24px",
                                        alignItems: "center",
                                        width: "100%"
                                    }
                                },
                                    usage.value.charts?.categories?.series?.length && AreaChart({
                                        series: usage.value.charts.categories.series,
                                        xaxis: { type: "datetime", tickAmount: 4 },
                                        yaxis: { show: false, labels: { show: false } }
                                    }) || MutedText("No usage data."),
                                    renderPieFromItems(
                                        usage.value.categoryDiscriminations,
                                        item => item.description,
                                        item => item.total
                                    )
                                )
                            ),
                            Section(
                                Table(
                                    { classList: ["hoverable"], pagination: { perPage: 20 } },
                                    TableHeader(
                                        TableHeaderCell("SKU"),
                                        TableHeaderCell("Quantity"),
                                        TableHeaderCell("Total usage")
                                    ),
                                    TableBody(
                                        usage.value.categoryDiscriminations.map(item =>
                                            TableRow(
                                                TableCell(InlineCode(item.description)),
                                                TableCell(formatNumber(item.quantity)),
                                                TableCell(formatPreciseCurrency(item.total), { title: item.total })
                                            )
                                        )?.nullWhenEmpty() || DefaultRow("No SKU usage in this period.")
                                    )
                                )
                            )
                        )
                    },
                    {
                        title: "Usage per resource",
                        icon: Icon("node-tree"),
                        contents: Content(
                            Section(
                                usage.value.charts?.resources?.series?.length && AreaChart({
                                    series: usage.value.charts.resources.series,
                                    xaxis: { type: "datetime", tickAmount: 4 },
                                    yaxis: { show: false, labels: { show: false } }
                                }) || MutedText("No resource usage data.")
                            ),
                            Section(
                                Table(
                                    { classList: ["hoverable"], pagination: { perPage: 30 } },
                                    TableHeader(
                                        TableHeaderCell("Resource"),
                                        TableHeaderCell("Total usage")
                                    ),
                                    usage.value.resourceDiscriminations.map(resourceGroup =>
                                        TableBody(
                                            TableRow(
                                                TableCell({ classList: ["separator"] }, resourceTypeName(resourceGroup))
                                            ),
                                            ...resourceGroup.items.map(resourceItem =>
                                                TableRow(
                                                    TableCell(
                                                        resourceItem.name || MutedText("Other"),
                                                        resourceItem.id && resourceItem.id !== "00000000-0000-0000-0000-000000000000" && el("div", MutedText(resourceItem.id))
                                                    ),
                                                    TableCell(formatPreciseCurrency(resourceItem.total), { title: resourceItem.total })
                                                )
                                            )
                                        )
                                    )?.nullWhenEmpty() || DefaultRow("No resource usage in this period.")
                                )
                            )
                        )
                    },
                    {
                        title: "Listings",
                        icon: Icon("list-check"),
                        contents: Content(
                            Section(
                                Table(
                                    { classList: ["hoverable"], pagination: { perPage: 50 } },
                                    TableHeader(
                                        TableHeaderCell("Id"),
                                        TableHeaderCell("Request"),
                                        TableHeaderCell("Moment"),
                                        TableHeaderCell("Description"),
                                        TableHeaderCell("Model"),
                                        TableHeaderCell("SKU"),
                                        TableHeaderCell("Quantity"),
                                        TableHeaderCell("Amount")
                                    ),
                                    TableBody(
                                        usage.value.listings.map((item, index, items) =>
                                            el.fragment(
                                                index > 0 && item.requestId !== items[index - 1].requestId && TableHorizontalBar(),
                                                TableRow(
                                                    TableCell(CopiableText("#" + item.id.substr(-4), item.id)),
                                                    TableCell(
                                                        Badge(CopiableText("#" + item.requestId.substr(-4), item.requestId), getUniqueColor(item.requestId))
                                                    ),
                                                    TableCell(formatDateTime(item.moment), { title: item.moment }),
                                                    TableCell(item.description || MutedText("Empty")),
                                                    TableCell(item.modelName && ModelBadge(item.modelName) || MutedText("Empty")),
                                                    TableCell(item.category ? InlineCode(item.category) : MutedText("Empty")),
                                                    TableCell(formatFloat(item.quantity)),
                                                    TableCell(formatPreciseCurrency(item.amount), {
                                                        title: `Unit price x1000: ${formatVeryPreciseCurrency(item.unitPrice * 1000)}, total: ${formatVeryPreciseCurrency(item.amount)}`
                                                    })
                                                )
                                            )
                                        )?.nullWhenEmpty() || DefaultRow("No usage listings in this period."),
                                        {
                                            mount() {
                                                this.closest("table").refreshPagination();
                                            }
                                        }
                                    )
                                )
                            )
                        )
                    },
                    {
                        title: "Monthly report",
                        icon: Icon("file-chart-line"),
                        contents: Content(
                            MonthlyReport(`/api/v1/admin/accounts/${accountId}/monthly-report`)
                        )
                    }
                ].filter(Boolean), { type: "fixed" });
            })
        )
    );
}
